import React, { useEffect, useState } from 'react';
import { List, Datagrid, FunctionField, SelectInput, DateInput, ReferenceInput, AutocompleteInput, useTranslate } from 'react-admin';
import { Box, Typography } from '@mui/material';
import { apiClient } from '../../utils/apiClient';
import { AlertStateChip, SeverityChip } from './AlertChips';
import { handledLine, when } from './alertText';
import { KIOSK_ALERT_STATUSES, type KioskAlert, type KioskAlertKindOption } from './types';

type Translate = (key: string, options?: any) => string;

export const KindFilterInput = ({ source, label }: { source: string; label: string }) => {
  const [choices, setChoices] = useState<{ id: string; name: string }[]>([]);
  useEffect(() => {
    let active = true;
    apiClient
      .get<{ data: KioskAlertKindOption[] }>('/api/backend/kiosk-alerts/kinds')
      .then((response) => {
        if (active) setChoices(response.data.map((option) => ({ id: option.kind, name: option.label })));
      })
      .catch((error) => console.error('[Kiosk alerts] the alert kinds could not be loaded', error));
    return () => {
      active = false;
    };
  }, []);
  return <SelectInput source={source} label={label} choices={choices} />;
};

export const kioskAlertFilters = (translate: Translate) => [
  <SelectInput
    key="status"
    source="status"
    label={translate('resources.kiosk-alerts.filters.status')}
    alwaysOn
    choices={[
      ...KIOSK_ALERT_STATUSES.map((status) => ({ id: status, name: translate(`resources.kiosk-alerts.statuses.${status}`) })),
      { id: 'all', name: translate('resources.kiosk-alerts.filters.all') },
    ]}
  />,
  <KindFilterInput key="kind" source="kind" label={translate('resources.kiosk-alerts.filters.kind')} />,
  <ReferenceInput key="siteId" source="siteId" reference="sites">
    <AutocompleteInput optionText="name" label={translate('resources.kiosk-alerts.filters.site')} />
  </ReferenceInput>,
  <ReferenceInput key="kioskId" source="kioskId" reference="kiosk-devices">
    <AutocompleteInput optionText="name" label={translate('resources.kiosk-alerts.filters.kiosk')} />
  </ReferenceInput>,
  <DateInput key="from" source="from" label={translate('resources.kiosk-alerts.filters.from')} />,
  <DateInput key="to" source="to" label={translate('resources.kiosk-alerts.filters.to')} />,
];

export const KioskAlertsList = () => {
  const translate = useTranslate();
  const field = (key: string) => translate(`resources.kiosk-alerts.fields.${key}`);
  return (
    <List
      resource="kiosk-alerts"
      filters={kioskAlertFilters(translate)}
      filterDefaultValues={{ status: 'open' }}
      sort={{ field: 'severity', order: 'ASC' }}
      perPage={25}
      title={translate('resources.kiosk-alerts.name')}
    >
      <Datagrid bulkActionButtons={false} rowClick="show">
        <FunctionField label={field('severity')} sortable={false} render={(record: KioskAlert) => <SeverityChip severity={record.severity} />} />
        <FunctionField
          label={field('alert')}
          sortable={false}
          render={(record: KioskAlert) => (
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>{record.label}</Typography>
              <Typography variant="caption" color="text.secondary">{record.summary}</Typography>
            </Box>
          )}
        />
        <FunctionField label={field('kiosk')} sortable={false} render={(record: KioskAlert) => record.kioskName} />
        <FunctionField label={field('site')} sortable={false} render={(record: KioskAlert) => record.siteName} />
        <FunctionField label={field('raisedAt')} sortable={false} render={(record: KioskAlert) => when(record.raisedAt)} />
        <FunctionField label={field('status')} sortable={false} render={(record: KioskAlert) => <AlertStateChip status={record.status} />} />
        <FunctionField label={field('outcome')} sortable={false} render={(record: KioskAlert) => handledLine(record, translate)} />
      </Datagrid>
    </List>
  );
};

export default KioskAlertsList;
